/**
 * 文案生成结果页面
 * Step 2：展示生成的文案列表，支持编辑、多选、保存
 */

import { useState } from 'react'
import {
  CheckCircle2,
  Loader2,
  Save,
  RefreshCw,
  ArrowRight,
  CheckSquare,
  Square,
} from 'lucide-react'
import type { GeneratedNote } from '@/hooks/useCopywriting'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { CopywritingNoteCard } from './CopywritingNoteCard'
import { toast } from 'sonner'

interface CopywritingResultsProps {
  notes: GeneratedNote[]
  isGenerating: boolean
  generationProgress: { current: number; total: number } | null
  error: string | null
  onUpdateNote: (id: string, updates: Partial<Pick<GeneratedNote, 'title' | 'content' | 'hashtags'>>) => void
  onToggleSelection: (id: string) => void
  onToggleSelectAll: () => void
  onSaveSelected: () => Promise<string[]>
  onContinueCreating: () => void
  onNavigateToNotes?: () => void
}

export function CopywritingResults({
  notes,
  isGenerating,
  generationProgress,
  error,
  onUpdateNote,
  onToggleSelection,
  onToggleSelectAll,
  onSaveSelected,
  onContinueCreating,
  onNavigateToNotes,
}: CopywritingResultsProps) {
  const [isSaving, setIsSaving] = useState(false)
  const [savedCount, setSavedCount] = useState(0)

  const savedTotal = notes.filter(n => n.savedNoteId).length
  const selectableNotes = notes.filter(n => !n.savedNoteId)
  const selectedCount = selectableNotes.filter(n => n.selected).length
  const allSelected = selectableNotes.length > 0 && selectableNotes.every(n => n.selected)

  // 保存选中的文案
  const handleSave = async () => {
    if (selectedCount === 0) {
      toast.error('请至少选择一篇未保存的文案')
      return
    }

    setIsSaving(true)
    try {
      const ids = await onSaveSelected()
      setSavedCount(ids.length)
      toast.success(`成功保存 ${ids.length} 篇文案到笔记库`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '保存失败')
    } finally {
      setIsSaving(false)
    }
  }

  // 生成中的加载状态（但如果已有结果，则显示结果 + 进度条）
  if (isGenerating && notes.length === 0) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex flex-col items-center justify-center gap-4 text-center">
            <Loader2 className="w-10 h-10 animate-spin text-primary" />
            <div>
              <h3 className="font-semibold text-lg">正在生成文案...</h3>
              {generationProgress ? (
                <p className="text-sm text-muted-foreground mt-1">
                  已生成 {generationProgress.current}/{generationProgress.total} 篇
                </p>
              ) : (
                <p className="text-sm text-muted-foreground mt-1">
                  AI 正在为您创作，请稍候
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  // 错误状态
  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="py-8">
          <div className="flex flex-col items-center justify-center gap-4 text-center">
            <div className="text-destructive text-lg">生成失败</div>
            <p className="text-sm text-muted-foreground">{error}</p>
            <Button variant="outline" onClick={onContinueCreating}>
              <RefreshCw className="mr-2 h-4 w-4" />
              返回重试
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  // 无结果状态
  if (notes.length === 0) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex flex-col items-center justify-center gap-4 text-center">
            <p className="text-muted-foreground">暂无生成结果</p>
            <Button variant="outline" onClick={onContinueCreating}>
              开始创作
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* 顶部操作栏 */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                {isGenerating ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin text-primary" />
                    正在生成中...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                    生成完成
                  </>
                )}
              </CardTitle>
              <CardDescription className="mt-1">
                {generationProgress ? (
                  <>
                    已生成 {generationProgress.current}/{generationProgress.total} 篇
                    {selectedCount > 0 && `，已选中 ${selectedCount} 篇`}
                  </>
                ) : (
                  <>
                    共 {notes.length} 篇文案，已选中 {selectedCount} 篇
                    {savedTotal > 0 && `，已保存 ${savedTotal} 篇（自动跳过）`}
                  </>
                )}
              </CardDescription>
            </div>

            <div className="flex items-center gap-2">
              {/* 全选/取消全选 */}
              <Button
                variant="outline"
                size="sm"
                onClick={onToggleSelectAll}
                disabled={isGenerating}
              >
                {allSelected ? (
                  <>
                    <Square className="mr-2 h-4 w-4" />
                    取消全选
                  </>
                ) : (
                  <>
                    <CheckSquare className="mr-2 h-4 w-4" />
                    全选
                  </>
                )}
              </Button>

              {/* 保存按钮 */}
              <Button
                onClick={handleSave}
                disabled={selectedCount === 0 || isSaving || isGenerating}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    保存中...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    保存选中 ({selectedCount})
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* 文案列表 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {notes.map((note, index) => (
          <CopywritingNoteCard
            key={note.id}
            note={note}
            index={index}
            onUpdate={onUpdateNote}
            onToggleSelection={onToggleSelection}
          />
        ))}
      </div>

      {/* 底部操作栏 */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            {savedCount > 0 && (
              <p className="text-sm text-green-600">
                ✓ 已保存 {savedCount} 篇文案到笔记库
              </p>
            )}
            {savedCount === 0 && <div />}

            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={onContinueCreating}
                disabled={isGenerating}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                继续创作
              </Button>

              {onNavigateToNotes && (
                <Button onClick={onNavigateToNotes}>
                  前往笔记库
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

